/**
 * Вход, подтверждение почты, восстановление, приглашения, переключение
 * организации.
 *
 * Схема токенов — docs/API.md §2, и она целиком на сервере: две куки
 * `HttpOnly`, access на 15 минут по `Path=/`, refresh на 30 скользящих суток по
 * `Path=/api/v1/auth/`. Скрипт их не читает и читать не может — поэтому здесь
 * нет ни одной работы с токеном, только вызовы. Сохранять что-либо из сессии в
 * `localStorage` нельзя: это ровно та копия, которая переживает выход.
 *
 * Права приходят с сервера (`Session.permissions`) и фронтом из роли не
 * выводятся: иначе разрешения живут в двух местах и расходятся в сторону
 * «показали кнопку, которой нельзя пользоваться».
 */

import { api, followServerRedirect } from './client';
import { safePath } from '../return-to';
import type { components } from './schema';

export type Session = components['schemas']['Session'];
export type Account = components['schemas']['Account'];
export type MembershipSummary = components['schemas']['MembershipSummary'];
export type LoginRequest = components['schemas']['LoginRequest'];
export type InvitationPreview = components['schemas']['InvitationPreview'];
export type PasswordResetPreview = components['schemas']['PasswordResetPreview'];

/**
 * Текущий пользователь, активная организация, роль и посчитанные сервером
 * права.
 *
 * `quiet` ставится на первой проверке при загрузке: отсутствие сессии там —
 * обычное состояние человека, который ещё не вошёл, а не событие «вас
 * выкинуло».
 */
export function getSession(signal?: AbortSignal, quiet = false): Promise<Session> {
  return api.get<Session>('/auth/session', { signal, quiet });
}

/**
 * Потоки, которые открывает ГОСТЬ, — форма входа, регистрация, подтверждение
 * адреса, восстановление пароля.
 *
 * У всех стоит `quiet`, и это не косметика. `401` здесь — обычный ответ формы:
 * не тот пароль, не подтверждён адрес, нужен второй фактор. Без `quiet` такой
 * ответ объявляется потерей сессии, оболочка уводит на вход — то есть туда, где
 * человек уже стоит, — и стирает набранное. Ошибку он при этом не увидит вовсе.
 *
 * Отдельно про подтверждение адреса: сервер отвечает на него кодом
 * `unauthenticated`, а он лечится обменом. Без `quiet` кабинет сходил бы в
 * `/auth/refresh` по живой куке прошлой сессии, повторил вход, получил тот же
 * отказ — и выбросил человека на форму входа вместо того, чтобы показать ему
 * «Адрес почты не подтверждён. Проверьте письмо.»
 */
const GUEST = { quiet: true } as const;

/**
 * Вход по почте и паролю.
 *
 * Ответ на неверный пароль и на несуществующий адрес одинаков
 * (`401 invalid_credentials`) — иначе форма входа становится способом узнать,
 * кто у нас обслуживается. На рынке, где мы ведём конкурирующие бренды в одной
 * панели, это прямая утечка, поэтому текст ошибки здесь не уточняется.
 *
 * При включённом втором факторе приходит `401 mfa_required` и `mfa_token`
 * в теле проблемы; продолжение — `loginMfa`.
 */
export function login(body: LoginRequest): Promise<Session> {
  return api.post<Session>('/auth/login', body, GUEST);
}

/** Второй фактор: код приложения или код восстановления. */
export function loginMfa(
  body: components['schemas']['MfaLoginRequest'],
): Promise<Session> {
  return api.post<Session>('/auth/login/mfa', body, GUEST);
}

/**
 * Вход через Яндекс ID — единственный провайдер РФ-версии (спека §5).
 *
 * Это не `fetch`, а уход браузера: эндпоинт отвечает `302` к провайдеру, и
 * ответ на редирект нам не принадлежит. `return_to` — путь **внутри кабинета**,
 * поэтому наружу уходит только `pathname` с поиском: полный адрес позволил бы
 * увести человека на чужой сайт после успешного входа.
 *
 * На портале агентства этой кнопки нет вовсе: `redirect_uri` не регистрируется
 * под произвольное число чужих доменов.
 */
export function startYandexLogin(returnTo?: string): Promise<void> {
  // Проверка общая с `main.tsx` и `Login.tsx`: голое `startsWith('/')`
  // пропускает `//evil.example/`, и это значение уехало бы в `return_to`
  // серверного редиректа — то есть чужой хост подставился бы уже после
  // успешной аутентификации. Сервер обязан проверять его и у себя.
  const query = new URLSearchParams({ return_to: safePath(returnTo) });
  // Не `window.location.assign` напрямую. Полная навигация выбрасывала человека
  // из приложения на голый JSON при любом отказе старта OAuth — провайдер не
  // подключён, лёг, сработало ограничение частоты: в окне браузера появлялся
  // `{"type":"https://seeno.ru/problems/not-found",…}` без оформления и без
  // кнопки назад. Сначала спрашиваем сервер, и уходим, только если он согласился
  // перенаправить; отказ показывается на форме входа.
  return followServerRedirect(`/auth/yandex/start?${query.toString()}`);
}

/**
 * Подтверждение почты. Один эндпоинт на четыре назначения токена: регистрация,
 * смена адреса, приглашение, присвоение аудита. При успехе сразу выставляются
 * куки — второго шага «теперь войдите» нет.
 */
export function verifyEmail(token: string): Promise<Session> {
  return api.post<Session>('/auth/email/verify', { token }, GUEST);
}

/** Отправить письмо с подтверждением ещё раз. */
export function resendEmailVerification(email: string): Promise<void> {
  return api.post<void>('/auth/email/verify/resend', { email }, GUEST);
}

/**
 * Запрос восстановления пароля. Ответ `202` независимо от того, есть такой
 * адрес или нет — по той же причине, что и у входа.
 */
export function forgotPassword(email: string): Promise<void> {
  return api.post<void>('/auth/password/forgot', { email }, GUEST);
}

/**
 * Проверка токена восстановления. **Токен при этом не расходуется.**
 *
 * Корпоративные почтовые сканеры (Microsoft Defender Safe Links, Proofpoint)
 * ходят по ссылкам до человека. Гашение при открытии ломало бы восстановление у
 * части клиентов молча и невоспроизводимо на нашей стороне, поэтому токен
 * гасится отправкой нового пароля, а не переходом.
 */
export function validatePasswordReset(token: string): Promise<PasswordResetPreview> {
  return api.post<PasswordResetPreview>(
    '/auth/password/reset/validate',
    { token },
    GUEST,
  );
}

/** Новый пароль по токену. Гасит токен и отзывает все семейства токенов. */
export function confirmPasswordReset(token: string, password: string): Promise<Session> {
  return api.post<Session>('/auth/password/reset/confirm', { token, password }, GUEST);
}

/**
 * Регистрация. Отвечает `202` и **письмом** — сессии здесь не бывает.
 *
 * Ответ одинаков для свободного и занятого адреса: занятому уходит письмо «на
 * вашу почту пытались зарегистрироваться», а не отказ. Иначе форма регистрации
 * становится способом узнать, кто у нас обслуживается, — ровно та же причина,
 * по которой не уточняется ошибка входа.
 *
 * Возвращала `Promise<Session>`, и вызывающий делал `onDone(await signup(...))`
 * — то есть заходил в кабинет с `undefined` вместо сессии. Тип теперь совпадает
 * с контрактом, и продолжение у регистрации ровно одно: сказать, что письмо
 * ушло, и назвать адрес.
 *
 * `invitation_token` совмещает подтверждение почты и приём приглашения одним
 * действием; `audit_session_token` присваивает аудит, запущенный из той же
 * браузерной сессии.
 */
export function signup(body: components['schemas']['SignupRequest']): Promise<void> {
  return api.post<void>('/auth/signup', body, GUEST);
}

/**
 * Приглашение до входа: что показать человеку, пришедшему по ссылке. Публичный
 * путь — страница рисуется раньше, чем появляется сессия.
 */
export function resolveInvitation(
  token: string,
  signal?: AbortSignal,
): Promise<InvitationPreview> {
  return api.get<InvitationPreview>(`/public/invitations/${encodeURIComponent(token)}`, {
    signal,
    quiet: true,
  });
}

/**
 * Принять приглашение. Требует уже подтверждённой почты, совпадающей с адресом
 * приглашения; контекст сразу переключается на новую организацию.
 */
export function acceptInvitation(token: string): Promise<Session> {
  return api.post<Session>('/invitations/accept', { token });
}

/**
 * Переключение активной организации — единственный способ сменить арендатора.
 * Организация живёт в токене, а не в параметре запроса: подставлять арендатора
 * из запроса значит отдать выбор арендатора клиенту.
 */
export function switchContext(organizationId: string): Promise<Session> {
  return api.post<Session>('/auth/context', { organization_id: organizationId });
}

export function logout(): Promise<void> {
  return api.post<void>('/auth/logout');
}
